import React, { useEffect, useMemo, useState } from "react";
import TextInput from "../Common/TextInput";
import ajaxSchool from "../../util/remote/ajaxSchool";
import ActivityLoader from "../Common/ActivityLoader";
import Notification from "../Common/Notification";
import { useSchoolTransactions } from "../../DataStore/FeesTransactionContext";
import { useTheme } from "../../DataStore/ThemeContext";
import { useBalance } from "../../DataStore/BalanceContext";
import RenderSecure from "../../util/RenderSecure";

/* ---------------- tiny helpers ---------------- */
const num = (v) => (v ? parseInt(String(v).replace(/[^0-9]/g, ""), 10) || 0 : 0);

const PaymentForm = ({ studentId }) => {
  const [info, setInfo] = useState("");
  const { getCashBankListing, cashBankListing } = useBalance();
  const { refresh } = useSchoolTransactions();

  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [paidAmounts, setPaidAmounts] = useState({});
  const [transactionDate, setTransactionDate] = useState("");
  const [transactionRef, setTransactionRef] = useState("");
  const [paymentAccount, setPaymentAccount] = useState("");
  const [deferredIncome, setDeferredIncome] = useState(false);
  const [loading, setLoading] = useState(false);

  const { setNotify,  currentNotify} = useTheme();

  useEffect(() => {
    getPaymentBreakdown(true);
    getCashBankListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const getPaymentBreakdown = async (clearInfo = true) => {
    if (clearInfo) setInfo("");

    const server_response = await ajaxSchool.fetchPaymentPreview(studentId);
    if (server_response.status === "OK") {
      const details = server_response.details || [];
      setPaymentBreakdown(details);

      // initialize paid amounts
      setPaidAmounts(
        details.reduce((acc, item) => {
          acc[item.fee_id] = "";
          return acc;
        }, {})
      );
    } else {
      setInfo(
        <Notification
          type="info"
          message={server_response.message}
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
      setPaymentBreakdown("404");
    }
  };

  const totals = useMemo(() => {
    const totalExpected = Array.isArray(paymentBreakdown)
      ? paymentBreakdown.reduce((sum, item) => sum + (Number(item.balance) || 0), 0)
      : 0;

    const totalPaid = Object.values(paidAmounts).reduce(
      (sum, v) => sum + (num(v) || 0),
      0
    );

    const overPaid = totalPaid > totalExpected;

    return { totalExpected, totalPaid, overPaid };
  }, [paymentBreakdown, paidAmounts]);

  const handleAmountChange = (feeId, amount) => {
    setPaidAmounts((prev) => ({
      ...prev,
      [feeId]: String(amount).replace(/[^0-9]/g, ""),
    }));
  };

  const handlePayFullAmount = () => {
    if (!Array.isArray(paymentBreakdown)) return;
    const full = paymentBreakdown.reduce((acc, item) => {
      acc[item.fee_id] = String(num(item.balance));
      return acc;
    }, {});
    setPaidAmounts(full);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!Array.isArray(paymentBreakdown)) return;

    const paymentData = paymentBreakdown
      .filter((item) => num(paidAmounts[item.fee_id]) > 0)
      .map((item) => ({
        fee_id: item.fee_id,
        amount: num(paidAmounts[item.fee_id]),
      }));

    if (paymentData.length === 0) {
      setInfo(
        <Notification
          message="Please enter at least one payment amount."
          type="danger"
          closable={false}
        />
      );
      return;
    }

    if(totals.overPaid && !deferredIncome) {
      setNotify(
        <Notification
          message="Overpayment detected. Please enable 'Deferred Income' to proceed."
          type="danger"
          closable={false}
        />
      );
      return;
    }

    const payload = {
      student_id: studentId,
      transaction_date: transactionDate,
      transaction_ref: transactionRef,
      payment_account: paymentAccount?.value,
      deferred_income: deferredIncome,
      payment_details: paymentData,
    };

    setLoading(true);
    const server_response = await ajaxSchool.payFees(payload);
    console.log(server_response);
    setLoading(false);

    if (server_response.status === "OK") {
      setNotify(
        <Notification message={server_response.message} type="success" closable={false} />
      );

      // reset
      setPaidAmounts(
        paymentBreakdown.reduce((acc, item) => {
          acc[item.fee_id] = "";
          return acc;
        }, {})
      );
      setTransactionDate("");
      setTransactionRef("");
      setPaymentAccount("");
      setDeferredIncome(false);

      await getPaymentBreakdown(false);
      await refresh();
    } else {
      setNotify(
        <Notification type="danger" message={server_response.message} closable={false} />
      );
    }
  };

  const RenderAccountField = () => {
    const options = [];
    if (Array.isArray(cashBankListing)) {
      cashBankListing.forEach((item) =>
        options.push({
          label: `${item.account_number} ${item.account_name}`,
          value: item.account_id,
        })
      );
    }
    return (
      <TextInput
        label="Payment Account"
        type="select"
        leftIcon="fa fa-university"
        placeholder={paymentAccount?.label || "Select account..."}
        options={options}
        onChange={(item) => setPaymentAccount(item)}
        leftComponent={
          <i className="fa fa-bank" onClick={() => getCashBankListing(true)} />
        }
        loading={!cashBankListing}
        wrap
      />
    );
  };

  if ((!paymentBreakdown || loading) && paymentBreakdown !== "404") {
    return <ActivityLoader />;
  }

  return (
    <>
      {info}
      {currentNotify}

     {Array.isArray(paymentBreakdown) && (
  <form method="post" onSubmit={handlePaymentSubmit}>
    <div className="row">
      {/* ===================== LEFT: Main entry area ===================== */}
      <div className="col-12 col-md-12">
        {/* Transaction Info */}
        <div
          className="card shadow-sm mb-3"
          style={{
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,.05)",
            overflow: "hidden",
          }}
        >
          <div
            className="card-body"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,123,255,.04), rgba(255,193,7,.03), rgba(40,167,69,.03))",
            }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <div className="font-weight-semibold" style={{ fontSize: 14 }}>
                  Transaction details
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Date, reference & payment account
                </div>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-lg-4 mb-2">
                <TextInput
                  type="date"
                  label="Payment Date"
                  leftIcon="fa fa-calendar-alt"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                  wrap
                />
              </div>

              <div className="col-lg-4 mb-2">
                <TextInput
                  type="text"
                  label="Transaction Reference"
                  placeholder="e.g. MTN-12345"
                  leftIcon="fa fa-hashtag"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  required
                  wrap
                />
              </div>

              <div className="col-lg-4 mb-2">
                <RenderAccountField />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-12 col-md-8">
        {/* Payment items */}
          {/* Payment Breakdown (cashier-friendly grouped rows) */}
        <div
          className="card shadow-sm mb-3"
          style={{
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,.05)",
            overflow: "hidden",
          }}
        >
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "rgba(0,0,0,.04)",
                }}
              >
                <i className="fa fa-receipt text-primary" />
              </div>
              <div>
                <div className="card-title mb-0">Payment breakdown</div>
                <small className="text-muted">Enter amounts per fee item</small>
              </div>
            </div>

            {/* optional micro-hint */}
            <small className="text-muted d-none d-md-block">
              Tip: Click input to highlight row
            </small>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive overflow-auto" style={{ maxHeight: 300}}>
              <table className="table mb-0" style={{ fontSize: 13 }}>
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    background: "#fff",
                    boxShadow: "0 1px 0 rgba(0,0,0,.06)",
                  }}
                >
                  <tr>
                    <th className="border-0" style={{ padding: "10px 14px" }}>
                      Fee item
                    </th>
                    <th className="border-0 text-right" style={{ padding: "10px 14px", width: 300 }}>
                      Amount paid
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paymentBreakdown.map((item) => {
                    const due = Number(item.balance) || 0;

                    return (
                      <tr
                        key={item.fee_id}
                        className="cashier-row"
                        style={{ borderTop: "1px solid rgba(0,0,0,.04)" }}
                      >
                        <td style={{ padding: "12px 14px", verticalAlign: "middle" }}>
                          <div className="d-flex align-items-center">
                            <div
                              className="d-flex align-items-center justify-content-center mr-3"
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 12,
                                background: "rgba(0,0,0,.04)",
                                flex: "0 0 auto",
                              }}
                            >
                              <i className="fa fa-tag text-muted" />
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div className="font-weight-semibold" style={{ lineHeight: 1.15 }}>
                                {item.fee_name}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                Due:{" "}
                                <span className="font-weight-semibold text-dark">
                                  UGX {due.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td
                          className="text-right"
                          style={{ padding: "10px 14px", verticalAlign: "middle", width: 300 }}
                        >
                          <div className="d-flex align-items-center justify-content-end" style={{ gap: 10 }}>
                            <button
                              type="button"
                              className="btn btn-light btn-sm border"
                              onClick={() => handleAmountChange(item.fee_id, String(due))}
                              style={{ borderRadius: 12, fontWeight: 600 }}
                              title="Fill with due amount"
                            >
                              Fill
                            </button>

                            <div style={{ width: 210 }}>
                              <TextInput
                                type="text"
                                placeholder="Amount"
                                leftIcon="fa fa-money-bill-wave"
                                value={paidAmounts[item.fee_id]}
                                onChange={(e) => handleAmountChange(item.fee_id, e.target.value)}
                                wrap
                                style={{ marginBottom: -8 }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  <tr style={{ borderTop: "1px solid rgba(0,0,0,.06)" }}>
                    <td
                      className="text-right text-muted"
                      style={{ padding: "12px 14px", fontWeight: 700 }}
                    >
                      Total due
                    </td>
                    <td
                      className="text-right text-primary"
                      style={{
                        padding: "12px 14px",
                        fontWeight: 800,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      UGX {totals.totalExpected.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* row highlight */}
            <style>{`
              .cashier-row:hover { background: rgba(0,123,255,.03); }
              .cashier-row:focus-within {
                background: rgba(0,123,255,.06);
                outline: 2px solid rgba(0,123,255,.12);
                outline-offset: -2px;
              }
            `}</style>
          </div>
        </div>
        </div>

      {/* ===================== RIGHT: Summary rail ===================== */}
      <div className="col-12 col-md-4">
        <div
          className="position-sticky"
          style={{ top: 90 }} // adjust if you have sticky PageTitle
        >
          <div
            className="card shadow-sm mb-3"
            style={{
              borderRadius: 18,
              border: "1px solid rgba(0,0,0,.05)",
              overflow: "hidden",
              height: "350px",
            }}
          >
            <div className="card-header">
              <div className="card-title mb-0">Summary</div>
              <small className="text-muted ml-1">Review before saving</small>
            </div>

            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Total due
                </div>
                <div className="font-weight-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>
                  UGX {totals.totalExpected.toLocaleString()}
                </div>
              </div>

              <div className="d-flex justify-content-between mb-3">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Total paid
                </div>
                <div
                  className={`font-weight-bold ${totals.overPaid ? "text-danger" : "text-success"}`}
                  style={{ fontSize: 16, fontVariantNumeric: "tabular-nums" }}
                >
                  UGX {totals.totalPaid.toLocaleString()}
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handlePayFullAmount}
                  style={{ borderRadius: 12 }}
                >
                  <i className="fa fa-wand-magic-sparkles mr-2" />
                  Pay full
                </button>

                <button
                  className="btn btn-success btn-sm"
                  type="submit"
                  disabled={!paymentAccount || totals.totalPaid <= 0 || loading}
                  style={{ borderRadius: 12 }}
                >
                  <i className="fa fa-check mr-2" />
                  Save payment
                </button>
              </div>

              <hr />

              <div className="text-muted" style={{ fontSize: 12 }}>
                <div className="mb-2">
                  <i className="fa fa-keyboard mr-2" />
                  Tip: Use <strong>Tab</strong> to jump between fields.
                </div>
                <div className="mb-2">
                  <i className="fa fa-bolt mr-2" />
                  Use <strong>Fill</strong> to copy due to paid per item.
                </div>
                <div>
                  <i className="fa fa-circle-info mr-2" />
                  Ensure account is selected before saving.
                </div>
              </div>
            </div>
          </div>

          {/* Deferred income in side panel */}
          {totals.overPaid && (
            <div
              className="card shadow-sm mb-3"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,.05)",
              }}
            >
              <div className="card-body">
                <div className="d-flex align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 12,
                      background: "rgba(255,193,7,.12)",
                      flex: "0 0 auto",
                    }}
                  >
                    <i className="fa fa-triangle-exclamation text-warning" />
                  </div>

                  <div style={{ flex: 1 }}>
                    <div className="font-weight-semibold">Overpayment detected</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Record excess as Deferred Income if needed.
                    </div>
                    <RenderSecure code={['STUDENT_FEES_ADVANCE']}>
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          id="deferredIncome"
                          className="form-check-input"
                          checked={deferredIncome}
                          onChange={() => setDeferredIncome(!deferredIncome)}
                        />
                        <label htmlFor="deferredIncome" className="form-check-label ml-2">
                          Record Deferred Income
                        </label>
                      </div>
                    </RenderSecure>

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </form>
)}


      {paymentBreakdown === "404" && <></>}
    </>
  );
};

export default PaymentForm;
